import { Component, OnInit, Input } from '@angular/core';
import { filter, map, switchMap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { TabsData } from '@flexus/ui-elements';
import { classifyDocumentFiles, coerceDocumentFilesArray } from '@flexus/utilities';

export interface SilFileSummaryShape {
	claim: number;
	created: string;
	description: unknown;
	filename: string;
	id: number;
	job: number;
	on_maven: boolean;
	purpose: string;
	thumbnail: string; //b64
	token: string;
}

/** Config for one document tab when using tenant-driven dynamic tabs (e.g. from required_documents). */
export interface DocumentTabConfig {
	targetId: string;
	display: string;
	purpose: string;
	displayMode?: 'image-grid' | 'file-grid';
}

@Component({
	selector: 'flx-view-documents',
	templateUrl: './view-documents.component.html',
	styleUrls: ['./view-documents.component.scss']
})
export class ViewDocumentsComponent implements OnInit {
	@Input() location: string;
	@Input() files$: Observable<SilFileSummaryShape[]>;
	@Input() quotations$: Observable<SilFileSummaryShape[]>;
	@Input() photos$: Observable<SilFileSummaryShape[]>;
	@Input() reports$: Observable<SilFileSummaryShape[]>;
	@Input() latestDocuments$: Observable<SilFileSummaryShape[]>;
	@Input() invoices$: Observable<SilFileSummaryShape[]>;
	@Input() selectDocsToView: string[];
	/** When set, tabs are built from this config and files$ is filtered by purpose per tab (dynamic mode). */
	@Input() documentTabsConfig: DocumentTabConfig[];
	/** When true, show Supporting Documents (catch-all) plus explicit sections derived from files$. */
	@Input() useDynamicTab = false;
	currentTab$: Observable<string>;
	tabs: TabsData[];
	tabFiles$: Record<string, Observable<SilFileSummaryShape[]>> = {};
	supportingDocuments$: Observable<SilFileSummaryShape[]> = of([]);
	useDynamicTabs = false;

	constructor() {}

	ngOnInit() {
		if (this.useDynamicTab && this.files$) {
			this.applyClassifiedDocumentSections(this.files$);
			this.tabs = [
				{ display: 'Supporting Documents', targetId: 'supporting-documents', show: true },
				{ display: 'Photos', targetId: 'photos', show: true },
				{ display: 'Quotations', targetId: 'quotations', show: true },
				{ display: 'Invoices', targetId: 'invoices', show: true },
				{ display: 'Reports', targetId: 'reports', show: true },
				{ display: 'Latest Documents', targetId: 'latest-documents', show: true }
			];
			this.currentTab$ = of('supporting-documents');
		} else if (this.documentTabsConfig?.length && this.files$) {
			this.useDynamicTabs = true;
			this.tabs = this.documentTabsConfig.map(t => ({
				display: t.display,
				targetId: t.targetId,
				show: true
			}));
			this.documentTabsConfig.forEach(tab => {
				const purposeNorm = this.normalizePurpose(tab.purpose);
				this.tabFiles$[tab.targetId] = this.files$.pipe(
					filter((arr): arr is SilFileSummaryShape[] => Array.isArray(arr)),
					map(arr => arr.filter(f => this.normalizePurpose(f.purpose) === purposeNorm))
				);
			});
			this.currentTab$ = of(this.documentTabsConfig[0]?.targetId ?? '');
		} else if (this.selectDocsToView) {
			const inputArray = this.selectDocsToView;
			const outputArray: TabsData[] = [];
			for (let i = 0; i <= inputArray.length - 1; i++) {
				outputArray.push({
					display: inputArray[i].charAt(0).toUpperCase() + inputArray[i].slice(1),
					targetId: inputArray[i],
					show: true
				});
			}
			this.tabs = outputArray;
		} else {
			this.tabs = [
				{ display: 'Photos', targetId: 'photos', show: true },
				{ display: 'Quotations', targetId: 'quotations', show: true },
				{ display: 'Invoices', targetId: 'invoices', show: true },
				{ display: 'Reports', targetId: 'reports', show: true },
				{ display: 'Latest Documents', targetId: 'latest-documents', show: true }
			];
		}
		if (!this.useDynamicTabs && !this.useDynamicTab) {
			this.currentTab$ = of('photos');
		}

		if (!this.useDynamicTabs && !this.useDynamicTab && this.reports$) {
			this.reports$ = this.reports$.pipe(
				filter(value => Array.isArray(value)),
				switchMap((reports: Array<SilFileSummaryShape>) => {
					if (reports.length > 0) {
						return of(reports);
					}
					return this.files$?.pipe(
						filter(value => Array.isArray(value)),
						map((files: Array<SilFileSummaryShape>) =>
							files.filter(entry => entry.purpose.toLowerCase() === 'report').map(report => ({ ...report, filename: `${report.filename}.pdf` }))
						)
					) ?? of([]);
				})
			) as Observable<SilFileSummaryShape[]>;
		}
	}

	private applyClassifiedDocumentSections(files$: Observable<SilFileSummaryShape[] | { files?: SilFileSummaryShape[]; data?: SilFileSummaryShape[] }>): void {
		const classified$ = files$.pipe(
			map(arrOrObj => coerceDocumentFilesArray(arrOrObj)),
			filter((arr): arr is SilFileSummaryShape[] => Array.isArray(arr)),
			map(files => classifyDocumentFiles<SilFileSummaryShape>(files, { purposeBasedPhotos: true }))
		);

		this.supportingDocuments$ = classified$.pipe(map(c => c.supportingDocuments));
		this.photos$ = classified$.pipe(map(c => c.photos));
		this.quotations$ = classified$.pipe(map(c => c.quotations));
		this.invoices$ = classified$.pipe(map(c => c.invoices));
		this.reports$ = classified$.pipe(map(c => c.reports));
		this.latestDocuments$ = classified$.pipe(map(c => c.latestDocuments));
	}

	handleTab(tabInfo) {
		this.currentTab$ = of(tabInfo.target);
	}

	normalizePurpose(p: string): string {
		return (p ?? '').trim().toLowerCase().replace(/\s+/g, '_');
	}

	getFilesForTab(tab: DocumentTabConfig | undefined): Observable<SilFileSummaryShape[]> {
		return tab ? (this.tabFiles$[tab.targetId] ?? of([])) : of([]);
	}

	getDisplayMode(tab: DocumentTabConfig | undefined): 'image-grid' | 'file-grid' {
		if (!tab) return 'file-grid';
		return tab.displayMode ?? (this.normalizePurpose(tab.purpose) === 'photo' ? 'image-grid' : 'file-grid');
	}

	hasFiles(files: SilFileSummaryShape[] | null | undefined): boolean {
		return Array.isArray(files) && files.length > 0;
	}
}
